import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ReferenceLine,
} from 'recharts';
import type { CurvaSData } from '@/hooks/useEvmCurvaS';

interface Props {
  data: CurvaSData;
}

import { fmtCurrencyInt as formatCurrency } from '@/lib/formatters';

const formatDateTick = (d: string) => {
  if (!d) return '';
  const parts = d.split('-');
  return parts.length >= 2 ? `${parts[1]}/${parts[0]?.slice(2)}` : d;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg p-3 shadow-md text-xs space-y-1">
      <p className="font-medium text-foreground">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium">{formatCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export const EvmCurvaSChart: React.FC<Props> = ({ data }) => {
  const { points, bac, eac, cpi } = data;

  const chartData = useMemo(() =>
    points.map(p => ({
      data: p.data,
      PV: Math.round(p.pv),
      EV: Math.round(p.ev),
      AC: Math.round(p.ac),
      'Forecast (EAC)': p.eac_proj != null ? Math.round(p.eac_proj) : undefined,
    })),
    [points]
  );

  const forecastStatus = useMemo(() => {
    if (cpi == null) return null;
    if (cpi >= 1) return { label: 'Dentro do orçamento', variant: 'outline' as const, color: 'text-success' };
    if (cpi >= 0.9) return { label: 'Atenção', variant: 'warning' as const, color: 'text-warning' };
    return { label: 'Estouro de custo', variant: 'expired' as const, color: 'text-expired' };
  }, [cpi]);

  if (chartData.length < 2) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Curva S Financeira (EVM)</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          {forecastStatus && (
            <Badge variant={forecastStatus.variant}>
              CPI {cpi?.toFixed(2)} · {forecastStatus.label}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="data"
                tickFormatter={formatDateTick}
                className="text-xs fill-muted-foreground"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={(v) => `R$ ${(v / 1_000_000).toFixed(1)}M`}
                className="text-xs fill-muted-foreground"
                tick={{ fontSize: 12 }}
                width={70}
                label={{ value: 'Valor acumulado (R$)', angle: -90, position: 'insideLeft', offset: -5, style: { fontSize: 12, fill: 'hsl(var(--muted-foreground))' } }}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="line"
              />

              {/* BAC reference line */}
              <ReferenceLine
                y={bac}
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="6 3"
                label={{ value: `BAC ${formatCurrency(bac)}`, position: 'right', fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />

              {/* PV — Planned Value */}
              <Line
                type="monotone"
                dataKey="PV"
                name="PV — Valor Planejado"
                stroke="hsl(var(--chart-planned))"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                connectNulls
              />

              {/* EV — Earned Value */}
              <Line
                type="monotone"
                dataKey="EV"
                name="EV — Valor Agregado"
                stroke="hsl(var(--chart-actual))"
                strokeWidth={2.5}
                dot={false}
                connectNulls
              />

              {/* AC — Actual Cost */}
              <Line
                type="monotone"
                dataKey="AC"
                name="AC — Custo Real"
                stroke="hsl(var(--chart-cost))"
                strokeWidth={2}
                dot={false}
                connectNulls
              />

              {/* EAC Forecast projection */}
              {eac != null && (
                <Line
                  type="monotone"
                  dataKey="Forecast (EAC)"
                  name={`Forecast (EAC ${formatCurrency(eac)})`}
                  stroke="hsl(var(--destructive))"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={false}
                  connectNulls
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Summary footer */}
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span>BAC: <strong className="text-foreground">{formatCurrency(bac)}</strong></span>
          {eac != null && (
            <span>EAC: <strong className={cpi != null && cpi < 1 ? 'text-destructive' : 'text-success'}>
              {formatCurrency(eac)}
            </strong></span>
          )}
          {eac != null && bac > 0 && (
            <span>Variação: <strong className={eac > bac ? 'text-destructive' : 'text-success'}>
              {eac > bac ? '+' : ''}{(((eac / bac) - 1) * 100).toFixed(1)}%
            </strong></span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
